import { Injectable } from '@angular/core';
import { ExecutionResult, LcsApiService } from '@lcs/shared/api';
import { OrderingOption } from 'generated/graphql-types';
import { Observable } from 'rxjs';
import { FilterExpression, FilterOption, serializeFilterExpression } from 'src/app/filters/filter-definition';
import { FilterOperations } from 'src/app/filters/filter-operation/filter-operations.enum';
import { FilterPrimitive } from 'src/app/filters/filter-primitives';
import { LogicalOperators } from 'src/app/filters/logical-operators.enum';
import { UserOrderingOptions, UserRegisterOptions } from '../models/user-register-options';
import { UsersFilterOption } from '../models/users-filter-option';
import { GetUsers, GetUsersVariables } from '../queries/generated/GetUsers';
import { getUsersQuery } from '../queries/users.graphql';

@Injectable({ providedIn: 'root' })
export class UserRegisterApiService {
   constructor(private apiService: LcsApiService) {}

   getUsers(
      endCursor: string | null,
      filters: UsersFilterOption[] | null,
      quickFilters: UserRegisterOptions | null,
      orderingOptions: UserOrderingOptions[] | null
   ): Observable<ExecutionResult<GetUsers>> {
      if (quickFilters) {
         if (quickFilters.showAllUsers) {
            filters = [
               {
                  field: 'AccountId',
                  operation: FilterOperations.EqualTo,
                  type: 'numeric',
                  value: 1,
               },
            ];
         }
      }

      const filterBuilderFilterExpression: FilterExpression | null =
         filters && filters.length > 0
            ? {
                 filterOptions: filters.map(this.convertUserFilterOption.bind(this)),
              }
            : { filterOptions: [] };

      if (quickFilters) {
         if (quickFilters.searchText) {
            const searchText = quickFilters.searchText.trim();
            const subExpression: FilterExpression = { filterOptions: [], logicalOperator: LogicalOperators.Or };
            subExpression.filterOptions.push({
               field: 'UserName',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            subExpression.filterOptions.push({
               field: 'Name',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            subExpression.filterOptions.push({
               field: 'EmailAddress',
               operation: FilterOperations.Contains,
               value: searchText,
            });
            filterBuilderFilterExpression.subExpressions = [subExpression];
         }
      }
      if (!quickFilters?.includeInactive) {
         filterBuilderFilterExpression.filterOptions.push({
            field: 'IsActive',
            operation: FilterOperations.EqualTo,
            value: true,
         });
      }
      const variables = this.getUsersVariables(endCursor, filterBuilderFilterExpression, orderingOptions);
      return this.apiService.query<GetUsers, GetUsersVariables>({
         query: getUsersQuery,
         variables,
      });
   }

   private convertUserFilterOption(option: UsersFilterOption): FilterOption {
      return {
         field: option.field,
         operation: option.operation,
         value: this.getFilterOptionValue(option),
      };
   }

   private getFilterOptionValue(option: UsersFilterOption): FilterPrimitive | FilterPrimitive[] | null {
      if (option.operation === FilterOperations.HasValue) {
         return option.value;
      }
      switch (option.type) {
         case 'user':
            return Array.isArray(option.value)
               ? option.value.map((v) => {
                    return v.userId;
                 })
               : option.value?.userId || null;
         case 'text-box':
         case 'date':
         case 'numeric':
         case 'boolean':
            return option.value;
      }
   }

   private getUsersVariables(
      after: string | null,
      filterBuilderFilters: FilterExpression | null,
      orderingOptions: OrderingOption[] | null
   ): GetUsersVariables {
      const variables: GetUsersVariables = {
         after,
         filters: this.buildFilterExpression(filterBuilderFilters),
         first: 50,
         orderingOptions,
      };

      return variables;
   }

   private buildFilterExpression(filterBuilderFilters: FilterExpression | null): string {
      const expressions: FilterExpression[] = filterBuilderFilters?.subExpressions ?? [];

      if (filterBuilderFilters) {
         return serializeFilterExpression({
            filterOptions: filterBuilderFilters.filterOptions,
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      }

      if (expressions.length > 1) {
         return serializeFilterExpression({
            filterOptions: [],
            logicalOperator: LogicalOperators.And,
            subExpressions: expressions,
         });
      } else if (expressions.length === 1) {
         return serializeFilterExpression(expressions[0]);
      } else {
         return '';
      }
   }
}
